/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Modal, Spacer, Spinner, useModal, useToasts } from '@geist-ui/react'
import { nanoid } from 'nanoid'
import { unmarshall } from '@aws-sdk/util-dynamodb'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { deepEqual } from 'utils/objects'
import { usePageVisibility } from 'hooks'
import StepAEnrollmentForm from './components/StepAEnrollmentForm'
import SignupComplete from './components/SignupComplete'
import { useAuth0 } from '@auth0/auth0-react'

export default function SearchByAddress() {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth0()
  const {
    setVisible: setRecentEnrollmentVisible,
    bindings: recentEnrollmentBindings,
  } = useModal()
  const [isCaptchaComplete, setIsCaptchaComplete] = useState(false)
  const [isAcknowledged, setIsAcknowledged] = useState(false)
  const [isConsentGiven, setIsConsentGiven] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUsed, setIsUsed] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [emailedInfo, setEmailedInfo] = useState()
  const [redirectLoading, setRedirectLoading] = useState(false)
  const [signupCompleteInfo, setSignupCompleteInfo] = useState({
    show: false,
    alreadyEnrolled: false,
    differentEmail: false,
    createdNewAccount: false,
  })
  const captchaRef = useRef('')
  const isVisible = usePageVisibility()
  const [toast, setToast] = useToasts()
  const { query, push: routerPush } = useRouter()
  const hasMounted = useRef(false)

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    getValues,
  } = useForm()

  const handleVisibilityChange = async () => {
    if (!isVisible && !isEnrolling) {
      const {
        first_name,
        last_name,
        phone_number,
        address,
        email,
        account_number,
      } = await getValues()

      const valArray = [
        first_name,
        last_name,
        address,
        phone_number,
        email,
        account_number,
      ]

      const formIsEmpty = !valArray.some((val) => !!val) // checks if any values are truthy and returns opposite boolean

      if (formIsEmpty) {
        return
      }

      const bouncedUser = {
        first_name,
        last_name,
        phone_number,
        email,
        address: address?.primary_line.toUpperCase(),
        account_id: account_number,
      }

      if (deepEqual(bouncedUser, emailedInfo)) {
        return
      }

      setEmailedInfo(bouncedUser)

      const payload = {
        failure_reason:
          'User did not complete form before exiting the signup page. The information they entered can be found below:',
        bounced_user: true,
        ...bouncedUser,
      }

      navigator.sendBeacon(
        `${process.env.NEXT_PUBLIC_API_URL}/enrollment/notify`,
        JSON.stringify(payload)
      )
    }
  }

  useEffect(() => {
    handleVisibilityChange()
  }, [isVisible])

  const findCustomer = async (info) => {
    const customer = await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API_URL}/customerId`,
      data: {
        address: info.address.primary_line.toUpperCase(),
        last_name: info.last_name.toUpperCase(),
      },
    }).catch(() => {
      // setToast({
      //   text: `We could not find your account. Please try a different name or address`,
      //   type: 'error',
      //   delay: 5000,
      // })
      // captchaRef.current.reset()
      // setIsCaptchaComplete(false)
      // setIsLoading(false)
      // setIsEnrolling(false)

      return
    })

    return customer?.data
  }

  const customerNotFound = (info) => {
    console.log(info)

    axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API_URL}/enrollment/notify`,
      data: {
        failure_reason:
          'Unable to automatically enroll customer in WaterPro. Could not find LWC Account. The user may have entered an incorrect last name or address.',
        first_name: info.first_name,
        last_name: info.last_name,
        email: info.email,
        phone_number: info.phone_number.replace(/[^0-9]/g, ''), // input mask adds in non-numerical characters to the value. This .replace() method strips them out
        address: info.address.primary_line.toUpperCase(),
        account_id: info.account_number,
      },
    }).then(() => {
      captchaRef.current?.reset()
      setIsLoading(false)
      redirectToSuccess()
    })
  }

  // Shared Auth0 signup function
  const signupToAuth0 = async (userData) => {
    const response = await axios({
      method: 'post',
      url: 'https://auth.waterproplan.com/dbconnections/signup',
      headers: {
        'Content-Type': 'application/json',
      },
      data: userData,
    })

    // Extract user_id from Auth0 response
    const userId =
      response.data?._id || response.data?.user_id || response.data?.id || null

    if (!userId) {
      throw new Error('Failed to get user ID from Auth0 signup')
    }

    return 'auth0|' + userId
  }

  // Handle Auth0 signup errors (user already exists)
  const handleAuth0SignupError = (err, tupleForLink = null) => {
    const auth0ErrorCode = err.response?.data?.code
    if (
      auth0ErrorCode === 'invalid_signup' ||
      auth0ErrorCode === 'user_exists'
    ) {
      setIsUsed(true)
      if (tupleForLink && typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(
            'pendingLinkTuple',
            JSON.stringify(tupleForLink)
          )
        } catch (storageErr) {
          console.warn('Failed to persist pending link tuple', storageErr)
        }
      }

      captchaRef.current?.reset()
      setIsCaptchaComplete(false)
      setRedirectLoading(false)
      setIsLoading(false)
      setIsEnrolling(false)

      setSignupCompleteInfo((b) => ({
        ...b,
        show: true,
      }))

      return true // Indicates user exists error was handled
    }
    return false
  }

  const enrollUser = async (
    userRegistrationData,
    tupleForLink = null,
    account = null
  ) => {
    console.log('sending to auth0')
    try {
      // const auth0Id =
      //   isAuthenticated && user
      //     ? user.sub
      //     : await signupToAuth0(userRegistrationData)

      if (account) {
        // Queue enrollment after successful Auth0 signup
        const enrollmentData = {
          first_name: userRegistrationData.user_metadata.first_name,
          last_name: userRegistrationData.user_metadata.last_name,
          account_address: userRegistrationData.user_metadata.address,
          email: userRegistrationData.email,
          phone_number: userRegistrationData.user_metadata.phone_number,
          account_id: account.account_id,
          enrollment_method: 'online_portal',
          // auth0_id: auth0Id,
        }

        if (isAuthenticated && user) {
          enrollmentData.auth0_id = user.sub
        }

        try {
          await axios({
            method: 'post',
            url: `${process.env.NEXT_PUBLIC_API_URL}/enrollment`,
            headers: {
              'Content-Type': 'application/json',
            },
            data: enrollmentData,
          })

          captchaRef.current?.reset()
          setIsLoading(false)
          redirectToSuccess()
        } catch (queueErr) {
          console.error('Failed to enroll user:', queueErr)
          setToast({
            text: `An error occurred while enrolling your account. Please refresh and try again.`,
            type: 'error',
            delay: 5000,
          })
          captchaRef.current?.reset()
          setIsCaptchaComplete(false)
          setRedirectLoading(false)
          setIsLoading(false)
          setIsEnrolling(false)
        }
      } else {
        // Fallback if account not provided
        captchaRef.current?.reset()
        setIsLoading(false)
        redirectToSuccess()
      }
    } catch (err) {
      const handled = handleAuth0SignupError(err, tupleForLink)
      if (!handled) {
        setToast({
          text: `An error occurred while creating your account. Please refresh and try again.`,
          type: 'error',
          delay: 5000,
        })
      }
      captchaRef.current?.reset()
      setIsCaptchaComplete(false)
      setRedirectLoading(false)
      setIsLoading(false)
      setIsEnrolling(false)
    }
  }

  const invalidCaptcha = () => {
    captchaRef.current?.reset()
    setIsLoading(false)
    setIsCaptchaComplete(false)
    setToast({
      text: `There was a problem verifying your captcha: ${verification[
        'error-codes'
      ].join(' ')}`,
      type: 'error',
      delay: 5000,
    })
  }

  const verifyCaptcha = () => {
    const token = { token: captchaRef.current?.getValue() }
    return axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API_URL}/verify`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: token,
    })
  }

  const noAccountMatchesName = (account, info) => {
    captchaRef.current?.reset()
    axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API_URL}/enrollment/notify`,
      data: {
        failure_reason: 'Name did not match account',
        first_name: info.first_name,
        last_name: info.last_name,
        phone_number: info.phone_number.replace(/[^0-9]/g, ''), // input mask adds in non-numerical characters to the value. This .replace() method strips them out
        account_id: account.account_id,
        address: info.address.primary_line.toUpperCase(),
      },
    }).then(() => {
      captchaRef.current.reset()
      setIsLoading(false)
      redirectToSuccess()
    })
  }

  const handleSignup = (account, info, skipRecaptcha = false) => {
    if (account?.last_name !== info.last_name.toUpperCase()) {
      console.log('no account matches name')
      noAccountMatchesName(account, info) // sends user to Thank You page and notifies Homeserve that the given name doesn't match the account number
    } else {
      console.log('verifying captcha')
      verifyCaptcha() // hits google's API to verify the captcha input as being human - this is useless since there is no server-side verification of the token
        .then((res) => {
          console.log('verification response: ', res)
          const verificationStatus = res.info?.verification?.success
          console.log('verificationStatus: ', verificationStatus)
          if (verificationStatus && !skipRecaptcha) {
            console.log('invalid captcha')
            invalidCaptcha() // resets the loading state and validation states related to the captcha
          } else {
            const userRegistrationData = {
              email: info.email,
              password: nanoid(),
              connection: process.env.NEXT_PUBLIC_DB_CONNECTION,
              emailVerified: true,
              user_metadata: {
                first_name: info.first_name,
                last_name: info.last_name,
                phone_number: info.phone_number.replace(/[^0-9]/g, ''), // input mask adds in non-numerical characters to the value. This .replace() method strips them out
                consent: 'received',
                account_id: account.account_id,
                address: info.address.primary_line.toUpperCase(),
              },
            }
            console.log('enrolling user')
            enrollUser(
              userRegistrationData,
              {
                account_id: account.account_id,
                last_name: info.last_name.toUpperCase(),
                street_address_1: info.address.primary_line.toUpperCase(),
                email: info.email,
                phone_number: info.phone_number.replace(/[^0-9]/g, ''),
                first_name: info.first_name,
              },
              account
            ) // initiates the process of signing user up through Auth0; on duplicate, we'll link post-login
          }
        })
    }
  }

  const reenrollAccount = (account, info) => {
    console.log('reenrolling account')
    const user = {
      account_id: account.account_id,
      first_name: info.first_name,
      last_name: info.last_name,
      phone_number: info.phone_number.replace(/[^0-9]/g, ''), // input mask adds in non-numerical characters to the value. This .replace() method strips them out
      address: info.address?.primary_line.toUpperCase(),
      email: info.email,
    }

    const payload = {
      reenroll: true,
      ...user,
    }

    return axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API_URL}/enrollment/notify`,
      data: payload,
    }).catch(() => {
      captchaRef.current?.reset()
      setIsCaptchaComplete(false)
      setRedirectLoading(false)
      setIsLoading(false)
      setIsEnrolling(false)

      setToast({
        text: `An error occurred while re-enrolling your account. Please refresh and try again.`,
        type: 'error',
        delay: 5000,
      })
    })
  }

  const handleExistingRecord = async (account, info) => {
    const currentlyEnrolled = account.transaction_code === '1'
    const emailMatches =
      !account.email || account.email.toUpperCase() === info.email.toUpperCase()

    if (emailMatches && !currentlyEnrolled) {
      await reenrollAccount(account, info)
      redirectToSuccess()
    } else {
      // If the account is currently enrolled, we need to sign up the user to Auth0
      if (currentlyEnrolled && !isAuthenticated) {
        const user = {
          email: info.email,
          password: nanoid(),
          connection: process.env.NEXT_PUBLIC_DB_CONNECTION,
          emailVerified: true,
          user_metadata: {
            first_name: info.first_name,
            last_name: info.last_name,
            phone_number: info.phone_number.replace(/[^0-9]/g, ''), // input mask adds in non-numerical characters to the value. This .replace() method strips them out
            consent: 'received',
            account_id: account.account_id,
            address: info.address.primary_line.toUpperCase(),
          },
        }
        try {
          const auth0Id = await signupToAuth0(user)

          captchaRef.current?.reset()
          setIsCaptchaComplete(false)
          setRedirectLoading(false)
          setIsLoading(false)
          setIsEnrolling(false)

          setSignupCompleteInfo((b) => ({
            ...b,
            show: true,
            alreadyEnrolled: currentlyEnrolled,
            differentEmail: !emailMatches,
            createdNewAccount: true,
          }))

          return
        } catch (err) {
          // User already exists error
          // Handled below
        }
      }

      captchaRef.current?.reset()
      setIsCaptchaComplete(false)
      setRedirectLoading(false)
      setIsLoading(false)
      setIsEnrolling(false)

      setSignupCompleteInfo((b) => ({
        ...b,
        show: true,
        alreadyEnrolled: currentlyEnrolled,
        differentEmail: !emailMatches,
      }))
    }
  }

  const onSubmit = async (info, skipRecaptcha = false) => {
    const lastEnrollment =
      new Date() - new Date(localStorage['enrollmentSubmitted'])
    const hourInMilliseconds = 60 * 60 * 1000

    if (lastEnrollment < hourInMilliseconds) {
      setRecentEnrollmentVisible(true)
    } else {
      console.log('submitting')
      setIsLoading(true)
      setIsEnrolling(true)

      const customer = await findCustomer(info) // searches customer db for LWC account based on user input and checks against current enrollments

      const existingRecord = !!customer?.transaction_code // if an transaction_code value is returned, then the account is currently (or has previously) enrolled in Waterpro

      if (!customer || Object.keys(customer).length === 0) {
        console.log('customer not found')
        customerNotFound(info) // emails homeserve with captured data and informs them the user could not be signed up automatically
      } else {
        const account = unmarshall(customer)
        if (existingRecord) {
          console.log('record exists')
          handleExistingRecord(account, info)
        } else {
          console.log('signing up')
          handleSignup(account, info, skipRecaptcha)
        }
      }
    }
  }

  function redirectToSuccess() {
    if (window.location.pathname !== '/success/') {
      routerPush('/success')
    }
    localStorage['enrollmentSubmitted'] = new Date().toISOString()
  }

  // Check for all three sessionStorage options that SignupComplete handles
  // and show SignupComplete inline if any exist
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    // Check for pendingLinkTuple (link flow)
    const pendingLinkTuple = window.sessionStorage.getItem('pendingLinkTuple')
    if (pendingLinkTuple) {
      try {
        const data = JSON.parse(pendingLinkTuple)
        // Validate that we have the required fields
        if (data?.account_id && data?.last_name && data?.street_address_1) {
          setSignupCompleteInfo((b) => ({
            ...b,
            show: true,
          }))
          return
        }
      } catch (err) {
        console.warn('Failed to parse pending link tuple:', err)
      }
    }
  }, [])

  useEffect(() => {
    // block only runs if if hasn't run before and posses the query values passeed from the wordpress enrollment form
    if (!hasMounted.current && query?.lastName) {
      setRedirectLoading(true)
      setIsLoading(true)
      setIsEnrolling(true)

      const address = query.streetAddress
      let primary_line = address
      let city
      let state
      let zip_code

      const addressParts = address.split(' - ')

      if (addressParts.length > 1) {
        primary_line = addressParts[0]

        const cityStateZip = addressParts[1].split(',')
        if (cityStateZip[0]) {
          city = cityStateZip[0]
          if (cityStateZip[1]) {
            const stateZip = cityStateZip[1].trim().split(/\s+/)
            state = stateZip[0] ? stateZip[0] : undefined
            zip_code = stateZip[1] ? stateZip[1] : undefined
          }
        }
      }

      onSubmit(
        {
          first_name: query.firstName,
          last_name: query.lastName,
          phone_number: query.phoneNumber,
          email: query.emailAddress,
          consent: true,
          guidelines: true,
          address: {
            primary_line,
            city,
            state,
            zip_code,
          },
          account_number: query.accountNumber,
        },
        true
      )

      hasMounted.current = true
    }
  }, [query])

  return (
    <>
      {redirectLoading || authLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '700px',
            width: '100%',
          }}
        >
          <Spinner />
        </div>
      ) : signupCompleteInfo.show ? (
        <SignupComplete
          alreadyEnrolled={signupCompleteInfo.alreadyEnrolled}
          differentEmail={signupCompleteInfo.differentEmail}
        />
      ) : (
        <StepAEnrollmentForm
          register={register}
          errors={errors}
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          isLoading={isLoading}
          isUsed={isUsed}
          setIsUsed={setIsUsed}
          isCaptchaComplete={isCaptchaComplete}
          setIsCaptchaComplete={setIsCaptchaComplete}
          isAcknowledged={isAcknowledged}
          setIsAcknowledged={setIsAcknowledged}
          isConsentGiven={isConsentGiven}
          setIsConsentGiven={setIsConsentGiven}
          captchaRef={captchaRef}
        />
      )}
      <Modal {...recentEnrollmentBindings}>
        <Modal.Content style={{ textAlign: 'center' }}>
          <p>
            You have recently signed up for leak protection, please allow us
            time to confirm your account. Thank you.
          </p>
        </Modal.Content>
        <Modal.Action passive onClick={() => setRecentEnrollmentVisible(false)}>
          Okay
        </Modal.Action>
      </Modal>
    </>
  )
}
